import React from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

const styles = `
.custom-alert-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: 1rem;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.custom-alert-modal {
  background-color: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 450px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: slideUp 0.3s ease;
  overflow: hidden;
}

@keyframes slideUp {
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.custom-alert-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.custom-alert-header.success {
  background-color: #f0fdf4;
  border-bottom-color: #86efac;
}

.custom-alert-header.error {
  background-color: #fef2f2;
  border-bottom-color: #fca5a5;
}

.custom-alert-header.warning {
  background-color: #fffbeb;
  border-bottom-color: #fcd34d;
}

.custom-alert-header.info {
  background-color: #eff6ff;
  border-bottom-color: #93c5fd;
}

.custom-alert-icon {
  flex-shrink: 0;
}

.custom-alert-icon.success {
  color: #16a34a;
}

.custom-alert-icon.error {
  color: #dc2626;
}

.custom-alert-icon.warning {
  color: #ea580c;
}

.custom-alert-icon.info {
  color: #2563eb;
}

.custom-alert-title {
  flex: 1;
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0;
}

.custom-alert-title.success {
  color: #16a34a;
}

.custom-alert-title.error {
  color: #dc2626;
}

.custom-alert-title.warning {
  color: #ea580c;
}

.custom-alert-title.info {
  color: #2563eb;
}

.custom-alert-close-btn {
  background: none;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.3s;
  flex-shrink: 0;
}

.custom-alert-close-btn:hover {
  opacity: 0.7;
}

.custom-alert-body {
  padding: 1.5rem;
}

.custom-alert-message {
  color: #374151;
  line-height: 1.6;
  margin: 0;
  white-space: pre-line;
}

.custom-alert-footer {
  padding: 1rem 1.5rem;
  background-color: #f9fafb;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

.custom-alert-btn {
  padding: 0.625rem 1.5rem;
  border-radius: 0.375rem;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  border: none;
}

.custom-alert-btn-primary {
  background-color: #5A9C9C;
  color: white;
}

.custom-alert-btn-primary:hover {
  background-color: #4A8080;
}

.custom-alert-btn-secondary {
  background-color: #e5e7eb;
  color: #374151;
}

.custom-alert-btn-secondary:hover {
  background-color: #d1d5db;
}

@media (max-width: 640px) {
  .custom-alert-modal {
    max-width: 100%;
    margin: 1rem;
  }
  
  .custom-alert-header {
    padding: 1rem;
  }
  
  .custom-alert-body {
    padding: 1rem;
  }
  
  .custom-alert-footer {
    padding: 0.75rem 1rem;
  }
}
`;

export default function CustomAlert({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  showConfirm = false,
  onConfirm,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar'
}) {
  if (!isOpen) return null;

  const getIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle size={28} />;
      case 'error':
        return <AlertCircle size={28} />;
      case 'warning':
        return <AlertTriangle size={28} />;
      case 'info':
      default:
        return <Info size={28} />;
    }
  };

  const getDefaultTitle = () => {
    switch (type) {
      case 'success':
        return 'Sucesso';
      case 'error':
        return 'Erro';
      case 'warning':
        return 'Atenção';
      case 'info':
      default:
        return 'Informação';
    }
  };

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    onClose();
  };

  return (
    <>
      <style>{styles}</style>
      <div className="custom-alert-overlay" onClick={onClose}>
        <div className="custom-alert-modal" onClick={(e) => e.stopPropagation()}>
          <div className={`custom-alert-header ${type}`}>
            <div className={`custom-alert-icon ${type}`}>
              {getIcon()}
            </div>
            <h2 className={`custom-alert-title ${type}`}>
              {title || getDefaultTitle()}
            </h2>
            <button
              className="custom-alert-close-btn"
              onClick={onClose}
              aria-label="Fechar"
            >
              <X size={20} />
            </button>
          </div>

          <div className="custom-alert-body">
            <p className="custom-alert-message">{message}</p>
          </div>

          <div className="custom-alert-footer">
            {showConfirm ? (
              <>
                <button
                  className="custom-alert-btn custom-alert-btn-secondary"
                  onClick={onClose}
                >
                  {cancelText}
                </button>
                <button
                  className="custom-alert-btn custom-alert-btn-primary"
                  onClick={handleConfirm}
                >
                  {confirmText}
                </button>
              </>
            ) : (
              <button
                className="custom-alert-btn custom-alert-btn-primary"
                onClick={onClose}
              >
                OK
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}